// frontend/src/components/Auth/RegisterForm.js
import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  PersonAdd,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const RegisterForm = ({ onSwitchToLogin, onRegisterSuccess }) => {
  const { register } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
    username: '',
    role: 'user',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one digit');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate password in real-time
    if (name === 'password') {
      setPasswordErrors(validatePassword(value));
    }

    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirm_password) {
      setError(t('auth.errors.passwordMismatch'));
      setLoading(false);
      return;
    }

    // Validate password strength
    const passwordValidationErrors = validatePassword(formData.password);
    if (passwordValidationErrors.length > 0) {
      setError(t('auth.errors.passwordRequirements'));
      setLoading(false);
      return;
    }

    try {
      const result = await register(formData);

      if (result.success) {
        if (onRegisterSuccess) {
          onRegisterSuccess();
        }
      } else {
        // Handle different types of errors
        if (typeof result.error === 'string') {
          setError(result.error);
        } else if (Array.isArray(result.error)) {
          setError(result.error.join(', '));
        } else if (result.error && typeof result.error === 'object') {
          // Handle validation errors from backend
          if (result.error.detail) {
            if (Array.isArray(result.error.detail)) {
              setError(result.error.detail.map(err => err.msg || err).join(', '));
            } else {
              setError(result.error.detail);
            }
          } else {
            setError(t('auth.errors.registrationFailed'));
          }
        } else {
          setError(t('auth.errors.registrationFailedGeneric'));
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(t('auth.errors.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = 
    formData.email &&
    formData.password &&
    formData.confirm_password &&
    formData.full_name &&
    formData.password === formData.confirm_password &&
    passwordErrors.length === 0;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 500,
        width: '100%',
        mx: 'auto',
        borderRadius: 2,
        position: 'relative',
        zIndex: 10,
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <PersonAdd sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" component="h1" gutterBottom>
          {t('auth.register.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.register.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          name="full_name"
          label={t('auth.register.fullName')}
          value={formData.full_name}
          onChange={handleChange}
          margin="normal"
          required
          autoComplete="name"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          name="username"
          label={t('auth.register.username')}
          value={formData.username}
          onChange={handleChange}
          margin="normal"
          autoComplete="username"
          helperText={t('auth.register.usernameHelper')}
        />

        <TextField
          fullWidth
          name="email"
          type="email"
          label={t('auth.register.email')}
          value={formData.email}
          onChange={handleChange}
          margin="normal"
          required
          autoComplete="email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="action" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>{t('auth.register.role')}</InputLabel>
          <Select
            name="role"
            value={formData.role}
            onChange={handleChange}
            label={t('auth.register.role')}
          >
            <MenuItem value="user">{t('auth.register.roles.user')}</MenuItem>
            <MenuItem value="researcher">{t('auth.register.roles.researcher')}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          name="password"
          type={showPassword ? 'text' : 'password'}
          label={t('auth.register.password')}
          value={formData.password}
          onChange={handleChange}
          margin="normal"
          required
          autoComplete="new-password"
          error={passwordErrors.length > 0}
          helperText={passwordErrors.length > 0 ? passwordErrors[0] : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          name="confirm_password"
          type={showConfirmPassword ? 'text' : 'password'}
          label={t('auth.register.confirmPassword')}
          value={formData.confirm_password}
          onChange={handleChange}
          margin="normal"
          required
          autoComplete="new-password"
          error={formData.confirm_password && formData.password !== formData.confirm_password}
          helperText={
            formData.confirm_password && formData.password !== formData.confirm_password
              ? t('auth.errors.passwordMismatch')
              : ''
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={!isFormValid || loading}
          sx={{ mt: 3, mb: 2, py: 1.5 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('auth.register.createAccount')
          )}
        </Button>

        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.or')}
          </Typography>
        </Divider>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.register.hasAccount')}{' '}
            <Link
              component="button"
              type="button"
              onClick={onSwitchToLogin}
              sx={{ textDecoration: 'none' }}
            >
              {t('auth.register.signIn')}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default RegisterForm;
